import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Native port of the v1 `Touchpoint` factory (account-v1/src/core/api/touchpoint.js).
 * WS5 #10 Stage B B4a: replaces the `$injector.get('Touchpoint')` grabs in
 * ft-touchpoint-container (`loadRegularHeader()` → `Touchpoint.get(url)`;
 * `updateRegularState()` → `Touchpoint.activate(model)`).
 *
 * Only `get` + `activate` are ported (the container's sole calls). The v1 factory's
 * activateVersion/installVersion/setGroupStatus/setStatus/canActivate/put have no
 * native consumer here (canActivate is its own factory — touchpoint-activator.util).
 *
 * `get` returns the response BODY (v1 `Touchpoint.get = $http.get` resolved the
 * $http envelope, so the container read `response.data`; the native call site drops
 * the `.data` unwrap). `activate` fires the PUT (fire-on-call via firstValueFrom —
 * HttpClient observables are cold) and resolves the pre-mutation `origModel`, 1:1
 * with v1 (the sole caller ignores the resolved value).
 *
 * DOCUMENTED (server-equivalent) divergences, per the V1Api convention: HttpClient
 * sends Content-Type `application/json` where $http sent `application/json;charset=utf-8`.
 */
@Injectable({ providedIn: 'root' })
export class TouchpointV1ApiService {
  private readonly http = inject(HttpClient);

  /** v1 `Touchpoint.get` (= $http.get) — resolves the response BODY. */
  get(url: string): Promise<any> {
    return firstValueFrom(this.http.get(url));
  }

  /** v1 `Touchpoint.activate(model)` — builds the activate URL, PUTs (no body), resolves the original model. */
  activate(model: any): Promise<any> {
    const emailTouchpointsUriMap: Record<string, string> = {
      finalistsEmail: 'emails/finalist',
      reminderEmail: 'emails/finalistreminder',
      activityEmail: 'emails/finalistactivity',
      winnerEmail: 'emails/winnernotification',
      'invitation-email': 'emails/invitation-email',
      'reminder-emails': 'emails/reminder-emails',
      'reengagement-emails': 'emails/reengagement-emails',
      homepageEmail: 'emails/homepage',
      followup: 'emails/followup',
    };

    const origModel = { ...model };
    const emailPostfix = emailTouchpointsUriMap[model.touchpointType];
    if (model.layer == 'photoadvocacy') {
      model.layer =
        emailPostfix ||
        model.touchpointType == 'poststay-email-image' ||
        model.touchpointType == 'pre-arrival-email' ||
        model.touchpointType == 'photo-river'
          ? 'photo-advocacy'
          : 'photocontest';
    }

    let url: string;
    if (
      model.touchpointType == 'confirmation' ||
      model.touchpointType == 'list' ||
      model.touchpointType == 'homepage' ||
      model.touchpointType == 'quote' ||
      model.touchpointType == 'contestPage'
    ) {
      url =
        '/api/' + model.propertyUuid + '/properties/layers/touchpoints/' +
        model.touchpoint +
        '/activate/' +
        model.isActive;
    } else {
      url =
        '/api/' + model.propertyUuid + '/properties/layers/' +
        model.layer +
        '/touchpoints/' +
        model.touchpoint +
        '/' +
        (emailPostfix || model.touchpointType) +
        '/activate/' +
        model.isActive;
    }
    return firstValueFrom(this.http.put(url, null)).then(() => origModel);
  }
}
